/**
 * OmologaModal — crea o modifica un'omologa di un produttore.
 *
 * STEP 3: layout base (parametri + ponti trasportatori/intermediari).
 * STEP 4 sotto-commit (2): aggiunti
 *  - mini-modal nested per creare un nuovo trasportatore/intermediario senza
 *    chiudere il modal omologa (pattern CambiaProduttoreModal applicato al
 *    contesto nested — z-50 nel modal omologa, z-60 nel mini-modal — gestione
 *    indipendente dei due overlay);
 *  - sezione "Documenti omologa" visibile solo in modalità edit (omologa
 *    esistente con id): badge stato_firma + azioni genera/invia/carica.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, Download, FileSignature, Send, Upload, X,
} from 'lucide-react';
import {
  apriFirmatoPdfOmologa,
  apriSchedaPdfOmologa,
  caricaFirmatoOmologa,
  createOmologa,
  generaPdfOmologa,
  getOmologa,
  inviaOmologa,
  updateOmologa,
} from '../api/omologhe';
import type { Omologa, OmologaStatoFirma } from '../types/omologa';
import { omologaUpdateSchema, type OmologaUpdateData } from '../schemas/omologa';
import Badge from './ui/Badge';
import OmologaFields, { FieldWrap, inputCls } from './OmologaForm';

interface Props {
  produttoreId: string;
  produttoreEmail?: string | null;
  omologa?: Omologa | null;  // se valorizzata → edit; altrimenti → create
  onClose: () => void;
  onSaved: (o: Omologa) => void;
}

// ── Badge stato_firma ────────────────────────────────────────────────────────

function statoFirmaBadgeVariant(
  s: OmologaStatoFirma,
): 'neutral' | 'info' | 'purple' | 'success' {
  switch (s) {
    case 'bozza': return 'neutral';
    case 'generata': return 'info';
    case 'inviata': return 'purple';
    case 'firmata': return 'success';
  }
}

function StatoFirmaBadge({ stato }: { stato: OmologaStatoFirma }) {
  return <Badge variant={statoFirmaBadgeVariant(stato)}>{stato}</Badge>;
}

// ── Sezione "Documenti omologa" ──────────────────────────────────────────────

function DocumentiOmologa({
  omologa, produttoreEmail, onUpdated,
}: {
  omologa: Omologa;
  produttoreEmail: string | null;
  onUpdated: (o: Omologa) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'gen' | 'send' | 'upload' | 'dl-scheda' | 'dl-firmato' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showSendForm, setShowSendForm] = useState(false);
  // Inizializzato lazy dalla prop; non sincronizziamo se la prop cambia
  // a runtime (caso impossibile: il modal è figlio di una pagina dedicata
  // al singolo produttore). Evita il pattern set-state-in-effect.
  const [destinatarioEmail, setDestinatarioEmail] = useState(produttoreEmail ?? '');
  const [oggetto, setOggetto] = useState('Scheda omologa per firma');
  const [corpo, setCorpo] = useState(
    'Buongiorno,\n\nin allegato la scheda omologa da firmare e restituire.\n\nCordiali saluti.',
  );

  const stato = omologa.stato_firma;
  const hasPdf = !!omologa.pdf_scheda_url;
  const hasFirmato = !!omologa.pdf_firmato_url;
  const rigeneraDisabled = stato === 'firmata';

  async function handleGenera() {
    setBusy('gen'); setErr(null);
    try {
      const updated = await generaPdfOmologa(omologa.id);
      onUpdated(updated);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail ?? t('richieste.omologa_modal.errore_genera'));
    } finally {
      setBusy(null);
    }
  }

  async function handleInvia() {
    if (!destinatarioEmail.trim()) {
      setErr(t('richieste.omologa_modal.errore_destinatario'));
      return;
    }
    setBusy('send'); setErr(null);
    try {
      const updated = await inviaOmologa(omologa.id, {
        destinatario_email: destinatarioEmail.trim(),
        oggetto: oggetto.trim() || undefined,
        corpo: corpo.trim() || undefined,
      });
      onUpdated(updated);
      setShowSendForm(false);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail ?? t('richieste.omologa_modal.errore_invio'));
    } finally {
      setBusy(null);
    }
  }

  async function handleUpload(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      setErr(t('richieste.omologa_modal.errore_file_grande'));
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErr(t('richieste.omologa_modal.errore_solo_pdf'));
      return;
    }
    setBusy('upload'); setErr(null);
    try {
      const updated = await caricaFirmatoOmologa(omologa.id, file);
      onUpdated(updated);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail ?? t('richieste.omologa_modal.errore_upload'));
    } finally {
      setBusy(null);
    }
  }

  // Download PDF via endpoint autenticato (apre in nuova tab). I path /storage
  // grezzi NON sono serviti staticamente: l'unica via è l'endpoint con RBAC.
  async function handleDownloadScheda() {
    setBusy('dl-scheda'); setErr(null);
    try {
      await apriSchedaPdfOmologa(omologa.id);
    } catch {
      setErr(t('richieste.omologa_modal.errore_apri_scheda'));
    } finally {
      setBusy(null);
    }
  }

  async function handleDownloadFirmato() {
    setBusy('dl-firmato'); setErr(null);
    try {
      await apriFirmatoPdfOmologa(omologa.id);
    } catch {
      setErr(t('richieste.omologa_modal.errore_apri_firmato'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="mt-5 pt-4 border-t border-stone-mid" data-testid="section-documenti-omologa">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileSignature size={14} className="text-ink-soft" />
          <h3 className="text-sm font-semibold text-ink-dark">{t('richieste.omologa_modal.documenti_titolo')}</h3>
          <StatoFirmaBadge stato={stato} />
        </div>
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-xs text-accent-danger">{err}</div>
      )}

      {/* PDF scheda omologa */}
      <div className="bg-stone-bg/60 border border-stone-mid rounded-md px-3 py-2.5 mb-2.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs">
            <p className="font-medium text-ink-dark">{t('richieste.omologa_modal.pdf_scheda')}</p>
            <p className="text-ink-soft mt-0.5">
              {hasPdf ? t('richieste.omologa_modal.generato') : t('richieste.omologa_modal.non_generato')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasPdf && (
              <button type="button" onClick={handleDownloadScheda} disabled={busy !== null}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-sage border border-sage rounded-md hover:bg-sage-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="link-scarica-pdf-scheda">
                <Download size={11} />{busy === 'dl-scheda' ? t('richieste.omologa_modal.apertura') : t('richieste.omologa_modal.scarica')}
              </button>
            )}
            <button type="button" onClick={handleGenera}
              disabled={busy !== null || rigeneraDisabled}
              title={rigeneraDisabled ? t('richieste.omologa_modal.rigenera_disabled_title') : undefined}
              data-testid="btn-genera-pdf"
              className="px-3 py-1 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {busy === 'gen' ? t('richieste.omologa_modal.generazione') : hasPdf ? t('richieste.omologa_modal.rigenera') : t('richieste.omologa_modal.genera_pdf')}
            </button>
          </div>
        </div>
      </div>

      {/* Invio per firma — opzionale, solo se c'è un PDF */}
      {hasPdf && (
        <div className="bg-stone-bg/60 border border-stone-mid rounded-md px-3 py-2.5 mb-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              <p className="font-medium text-ink-dark">{t('richieste.omologa_modal.invio_firma')}</p>
              {omologa.firma_inviata_at ? (
                <p className="text-ink-soft mt-0.5">
                  {t('richieste.omologa_modal.inviata_il', { data: new Date(omologa.firma_inviata_at).toLocaleString('it-IT') })}
                </p>
              ) : (
                <p className="text-ink-soft mt-0.5">{t('richieste.omologa_modal.non_inviata')}</p>
              )}
            </div>
            {!showSendForm && (
              <button type="button" onClick={() => setShowSendForm(true)}
                disabled={busy !== null}
                data-testid="btn-invia-firma"
                className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-40">
                <Send size={11} />{omologa.firma_inviata_at ? t('richieste.omologa_modal.reinvia') : t('richieste.omologa_modal.invia_firma')}
              </button>
            )}
          </div>
          {showSendForm && (
            <div className="mt-3 space-y-2 border-t border-stone-mid pt-3">
              <FieldWrap label={t('richieste.omologa_modal.destinatario')}>
                <input className={inputCls} type="email" value={destinatarioEmail}
                  onChange={(e) => setDestinatarioEmail(e.target.value)}
                  placeholder="email@destinatario.it" />
              </FieldWrap>
              <FieldWrap label={t('richieste.omologa_modal.oggetto')}>
                <input className={inputCls} value={oggetto}
                  onChange={(e) => setOggetto(e.target.value)} />
              </FieldWrap>
              <FieldWrap label={t('richieste.omologa_modal.corpo')}>
                <textarea className={inputCls} rows={3} value={corpo}
                  onChange={(e) => setCorpo(e.target.value)} />
              </FieldWrap>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowSendForm(false)} disabled={busy !== null}
                  className="px-3 py-1 text-xs text-ink-mid border border-stone-border rounded-md hover:bg-stone-bg transition-colors disabled:opacity-50">
                  {t('common.azioni.annulla')}
                </button>
                <button type="button" onClick={handleInvia} disabled={busy !== null}
                  data-testid="btn-conferma-invio"
                  className="px-3 py-1 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60">
                  {busy === 'send' ? t('richieste.omologa_modal.invio') : t('richieste.omologa_modal.invia')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Carica firmato — sempre visibile */}
      <div className="bg-stone-bg/60 border border-stone-mid rounded-md px-3 py-2.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs">
            <p className="font-medium text-ink-dark">{t('richieste.omologa_modal.pdf_firmato')}</p>
            {hasFirmato ? (
              <p className="text-accent-success mt-0.5 flex items-center gap-1">
                <CheckCircle2 size={11} />
                {t('richieste.omologa_modal.caricato')} {omologa.firma_caricata_at
                  ? t('richieste.omologa_modal.caricato_il', { data: new Date(omologa.firma_caricata_at).toLocaleString('it-IT') })
                  : ''}
              </p>
            ) : (
              <p className="text-ink-soft mt-0.5">{t('richieste.omologa_modal.non_caricato')}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasFirmato && (
              <button type="button" onClick={handleDownloadFirmato} disabled={busy !== null}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-sage border border-sage rounded-md hover:bg-sage-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="link-scarica-pdf-firmato">
                <Download size={11} />{busy === 'dl-firmato' ? t('richieste.omologa_modal.apertura') : t('richieste.omologa_modal.scarica')}
              </button>
            )}
            <label
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors cursor-pointer"
              data-testid="label-carica-firmato">
              <Upload size={11} />
              {busy === 'upload' ? t('richieste.omologa_modal.upload') : hasFirmato ? t('richieste.omologa_modal.sostituisci') : t('richieste.omologa_modal.carica_firmato')}
              <input type="file" accept="application/pdf" className="hidden"
                disabled={busy !== null}
                data-testid="input-file-firmato"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  // reset per consentire reupload dello stesso file
                  e.currentTarget.value = '';
                }} />
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Componente principale ────────────────────────────────────────────────────

export default function OmologaModal({
  produttoreId, produttoreEmail, omologa: omologaProp, onClose, onSaved,
}: Props) {
  const { t } = useTranslation();
  const isEdit = !!omologaProp;
  const [omologa, setOmologa] = useState<Omologa | null>(omologaProp ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const { register, handleSubmit, control, setValue, getValues, formState: { errors } } =
    useForm<OmologaUpdateData>({
      resolver: zodResolver(omologaUpdateSchema),
      defaultValues: {
        numero_conferimenti: omologa?.numero_conferimenti ?? null,
        frequenza: omologa?.frequenza ?? '',
        pezzatura: omologa?.pezzatura ?? '',
        note: omologa?.note ?? '',
        data_firma: omologa?.data_firma ?? '',
        data_scadenza: omologa?.data_scadenza ?? '',
        trasportatori_ids: omologa?.trasportatori.map((tr) => tr.id) ?? [],
        intermediari_ids: omologa?.intermediari.map((i) => i.id) ?? [],
      },
    });

  const onSubmit = async (data: OmologaUpdateData) => {
    setIsSaving(true);
    setGlobalError(null);
    const payload = {
      produttore_id: produttoreId,
      numero_conferimenti: data.numero_conferimenti != null ? Number(data.numero_conferimenti) : null,
      frequenza: data.frequenza?.trim() || null,
      pezzatura: data.pezzatura?.trim() || null,
      note: data.note?.trim() || null,
      data_firma: data.data_firma?.trim() || null,
      data_scadenza: data.data_scadenza?.trim() || null,
      trasportatori_ids: data.trasportatori_ids,
      intermediari_ids: data.intermediari_ids,
    };
    try {
      const saved = isEdit && omologa
        ? await updateOmologa(omologa.id, payload)
        : await createOmologa(payload);
      setOmologa(saved);
      onSaved(saved);
    } catch {
      setGlobalError(t('richieste.omologa_modal.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  };

  async function handleDocsUpdated(updated: Omologa) {
    // refresh ottimistico: aggiorna il modal con la risposta del server
    setOmologa(updated);
    onSaved(updated);
    // ricarica il fresh state dal server (truth source) in background
    try {
      const fresh = await getOmologa(updated.id);
      setOmologa(fresh);
    } catch {
      // se fallisce, manteniamo lo stato ottimistico
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="modal-omologa">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink-dark">
              {isEdit ? t('richieste.omologa_modal.titolo_modifica') : t('richieste.omologa_modal.titolo_nuova')}
            </h2>
            {isEdit && omologa && <StatoFirmaBadge stato={omologa.stato_firma} />}
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 overflow-y-auto" data-testid="form-omologa">
          {globalError && (
            <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">{globalError}</div>
          )}

          <OmologaFields
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
          />

          {/* Sezione "Documenti omologa" — solo in modalità edit, dopo che
              l'omologa è stata salvata almeno una volta. In create-new restiamo
              minimali: prima si salvano i dati, poi si lavora sui documenti. */}
          {isEdit && omologa && (
            <DocumentiOmologa
              omologa={omologa}
              produttoreEmail={produttoreEmail ?? null}
              onUpdated={handleDocsUpdated}
            />
          )}

          {!isEdit && (
            <p className="mt-4 text-xs text-ink-soft italic">
              {t('richieste.omologa_modal.hint_salva')}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-stone-mid">
            <button type="button" onClick={onClose} disabled={isSaving}
              className="px-4 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors disabled:opacity-50">
              {isEdit ? t('richieste.omologa_modal.chiudi') : t('common.azioni.annulla')}
            </button>
            <button type="submit" disabled={isSaving} data-testid="btn-salva-omologa"
              className="px-5 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSaving ? t('richieste.omologa_modal.salvataggio') : isEdit ? t('richieste.omologa_modal.salva_modifiche') : t('richieste.omologa_modal.crea')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
